import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users,
  Zap,
  Crown,
  Target,
  TrendingUp,
  DollarSign,
  Calendar,
  Shield,
  Rocket,
  Star,
  Brain,
  Network,
  Layers,
  Activity,
  Gem,
  Award,
  Timer,
  Banknote,
  ChevronRight,
  Play,
  RefreshCw,
  Info,
  CheckCircle
} from 'lucide-react';
import WrapUpP2P from './layouts/WrapUpP2p';
import { useDispatch, useSelector } from 'react-redux';
import { allRemoteSettingsSelect } from '../../../redux/selectors';
import { money } from '../../../utility/Helper1';
import { exchangeActions } from '../../../redux/actions';
import notify from '../../../utility/NotificationSystem';

const P2PInvestmentCommandCenter = () => {
 
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams();

  const site_settings = useSelector(allRemoteSettingsSelect);
  const currencies = site_settings?.plans;
  const [selectedCurrency, setSelectedCurrency] = useState(currencies?.[0]?.mother?.type);
  const allPlans = site_settings?.plans?.filter(el => el.mother?.type == selectedCurrency);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [amount, setAmount] = useState(1000);
  const [submittingInvestment, setSubmittingInvestment] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const navigate = useNavigate()
  
  // Get recommit parameter from URL
  const recommitFor = searchParams.get('recommit_for');

  const selectedCurrencyObj = allPlans?.[0]?.mother;

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationPhase(prev => (prev + 1) % 100);
    }, 100);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Auto-select first plan when currency changes
    if (allPlans?.[0]?.childs?.length > 0) {
      setSelectedPlan(allPlans[0].childs[0]);
      setAmount(allPlans[0].childs[0].p2p_minimumdonation);
    }
  }, [selectedCurrency]);

  const calculateReturns = () => {
    if (!selectedPlan) return { returns: 0, total: 0 };
    
    const investmentDays = parseInt(selectedPlan?.p2p_ghdate) || 30;
    const periodRoi = parseFloat(selectedPlan?.p2p_roi ?? 0);
    const investmentAmount = parseFloat(amount);
    const returns = (investmentAmount * periodRoi / 100);
    const total = investmentAmount + returns;
    
    return {
      returns,
      total,
      periodRoi: periodRoi?.toFixed(1),
      dailyReturn: (returns / investmentDays).toFixed(2)
    };
  };

  const projectedReturns = calculateReturns();

  const isAmountValid = selectedPlan && 
    parseFloat(amount) >= parseFloat(selectedPlan.p2p_minimumdonation) && 
    parseFloat(amount) <= parseFloat(selectedPlan.p2p_maximumdonation);

  const submitInvestment = () => {
    setSubmittingInvestment(true)
    const data = {
      amount,
      plan: selectedPlan?.type,
      ...(recommitFor && { recommitment_for: recommitFor })
    }

    dispatch(exchangeActions.onPostExchange(data, "p2p/exchange/new", response => {
      setSubmittingInvestment(false)
      notify(response.msg, response.code)
      if(response.code == 1){
        navigate('/all/deposit/withdrawal');
      }
    }))
  }

  const getTierGradient = (tier) => {
    switch(tier) {
      case 'bronze': return 'from-orange-600 via-amber-500 to-yellow-600';
      case 'silver': return 'from-gray-400 via-amber-300 to-yellow-400';
      case 'gold': return 'from-yellow-400 via-amber-400 to-orange-500';
      default: return 'from-amber-400 to-orange-500';
    }
  };

  const NetworkCard = ({ plan, isActive, onClick }) => {
    return (
      <div
        onMouseDown={(e) => {
          e.preventDefault();
          onClick();
        }}
        className={`relative cursor-pointer select-none group transition-all duration-200 ${
          isActive ? 'scale-105' : 'hover:scale-[1.02]'
        }`}
      >
        {/* Glow Effect */}
        <div className={`absolute inset-0 bg-gradient-to-br ${getTierGradient(plan.tier)} rounded-xl blur-md transition-opacity duration-200 ${
          isActive ? 'opacity-60' : 'opacity-0 group-hover:opacity-30'
        }`}></div>
        
        {/* Main Card */}
        <div className={`relative bg-gray-900/95 backdrop-blur-xl border rounded-xl p-5 transition-all duration-200 ${
          isActive 
            ? `border-amber-400 shadow-xl shadow-amber-500/20` 
            : 'border-gray-600 group-hover:border-amber-500/60'
        }`}>
          
          {/* Header with Selection Indicator */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              {/* Elegant Selection Indicator */}
              <div className={`relative w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                isActive 
                  ? 'border-amber-400 bg-gradient-to-r from-amber-400 to-orange-500' 
                  : 'border-gray-500 group-hover:border-amber-400/60'
              }`}>
                {isActive && (
                  <>
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 animate-pulse"></div>
                    <div className="absolute inset-1 rounded-full bg-white flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500"></div>
                    </div>
                  </>
                )}
              </div>
              
              <div>
                <h4 className="text-white font-bold text-base">{plan.name}</h4>
                <p className="text-gray-400 text-xs capitalize">{plan.tier} tier</p>
              </div>
            </div>
            
            {/* ROI Badge */}
            <div className={`px-3 py-1 rounded-lg font-bold text-lg transition-colors duration-200 ${
              isActive 
                ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white' 
                : 'bg-gray-700 text-amber-400 group-hover:bg-amber-500/20'
            }`}>
              {plan.p2p_roi}%
            </div>
          </div>
          
          {/* Duration Info */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-amber-400" />
              <span className="text-gray-400 text-sm">Duration</span>
            </div>
            <span className="text-white font-semibold">{plan.p2p_ghdate} days</span>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full bg-gradient-to-r ${getTierGradient(plan.tier)} transition-all duration-500 ${
                  isActive ? 'animate-pulse' : ''
                }`}
                style={{ width: `${Math.min(100, (plan.p2p_roi / 35) * 100)}%` }}
              ></div>
            </div>
          </div>
          
          {/* Selected Badge */}
          {isActive && (
            <div className="absolute -top-2 -right-2">
              <div className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-full p-2 shadow-lg animate-bounce">
                <Star className="h-4 w-4 text-white" />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <WrapUpP2P allowAutoPadding={false}>
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800 p-6">
      {/* Floating Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-64 h-64 bg-gradient-to-r from-amber-500/5 to-orange-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-gradient-to-r from-yellow-500/5 to-amber-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Recommit Notification */}
        {recommitFor && (
          <div className="mb-6 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 backdrop-blur-xl border border-blue-500/30 rounded-2xl p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <RefreshCw className="h-6 w-6 text-blue-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-blue-400" />
                  <h3 className="text-lg font-bold text-white">Recommitment Mode</h3>
                </div>
                <p className="text-blue-100">
                  This bid is being created to recommit for transaction ID: 
                  <span className="font-mono text-blue-300 ml-2 bg-blue-500/20 px-2 py-1 rounded">
                    {recommitFor}
                  </span>
                </p>
                <p className="text-blue-200 text-sm mt-1">
                  The bid with txnID {recommitFor} will be automatically marked 'recommitted' when this bid is paid.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 space-y-4 lg:space-y-0">
          
          {currencies?.length > 1 && <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 bg-gray-800/80 backdrop-blur-xl border border-amber-500/30 rounded-xl px-4 py-2">
              <Banknote className="h-5 w-5 text-amber-400" />
              <select
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="bg-transparent text-white font-medium focus:outline-none"
              >
                {currencies?.map(currency => (
                  <option key={currency?.mother?.abbrRo} value={currency?.mother?.type} className="bg-gray-800">
                    {currency?.mother?.abbrRo}
                  </option>
                ))}
              </select>
            </div>
          </div>}

        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-12 gap-6">

          {/* Left Panel - Network Selection */}
          {allPlans[0].childs?.length > 1 && <div className="col-span-12 lg:col-span-3 space-y-4">
            <div className="bg-gray-900/60 backdrop-blur-xl border border-amber-500/20 rounded-2xl p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Users className="h-6 w-6 text-amber-400" />
                <h3 className="text-xl font-bold text-white">Networks</h3>
              </div>
              
              <div className="space-y-3">
                {allPlans?.[0]?.childs?.map((plan) => (
                  <NetworkCard
                    key={plan.type}
                    plan={plan}
                    isActive={selectedPlan?.type === plan.type}
                    onClick={() => {
                      setSelectedPlan(plan);
                      setAmount(plan.p2p_minimumdonation);
                    }}
                  />
                ))}
              </div>
            </div>
          </div>}

          {/* Center Panel - Investment Interface */}
          <div className="col-span-12 lg:col-span-6">
            
            {/* Investment Control Panel */}
            <div className="bg-gray-900/60 backdrop-blur-xl border border-amber-500/20 rounded-3xl p-8 mb-6">
              <div className="flex items-center space-x-3 mb-8">
                <Zap className="h-8 w-8 text-amber-400" />
                <h2 className="text-3xl font-bold text-white">
                  {recommitFor ? 'Recommitment Control' : 'Investment Control'}
                </h2>
              </div>

              {selectedPlan && (
                <div className="space-y-8">
                  {/* Selected Network Info */}
                  <div className={`bg-gradient-to-r ${getTierGradient(selectedPlan.tier)} p-1 rounded-2xl`}>
                    <div className="bg-gray-900 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-white">{selectedPlan.name}</h3>
                          <p className="text-gray-400 capitalize">{selectedPlan.tier} Network</p>
                        </div>
                        <div className="text-right">
                          <span className="text-3xl font-black text-amber-400">{Number(selectedPlan.p2p_roi) - 100}%</span>
                          <p className="text-gray-400 text-sm">ROI</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <Calendar className="h-5 w-5 text-amber-400 mx-auto mb-2" />
                          <p className="text-white font-bold">{selectedPlan.p2p_ghdate}</p>
                          <p className="text-gray-400 text-xs">Days</p>
                        </div>
                        <div>
                          <TrendingUp className="h-5 w-5 text-amber-400 mx-auto mb-2" />
                          <p className="text-white font-bold">100%</p>
                          <p className="text-gray-400 text-xs">Security</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Amount Input */}
                  <div className="space-y-4">
                    <label className="block text-white font-bold text-lg">
                      {recommitFor ? 'Recommitment Amount' : 'Investment Amount'}
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl blur group-focus-within:opacity-100 opacity-0 transition-opacity duration-300"></div>
                      <div className="relative flex items-center bg-gray-800 border border-gray-600 rounded-2xl focus-within:border-amber-500 transition-colors duration-300">
                        <div className="absolute left-6 flex items-center pointer-events-none">
                          <span className="text-amber-400 font-bold text-xl">{selectedCurrencyObj?.abbrRo}</span>
                        </div>
                        <input
                          type="number"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          min={selectedPlan.p2p_minimumdonation}
                          max={selectedPlan.p2p_maximumdonation}
                          className="w-full pl-20 pr-6 py-6 text-2xl font-bold bg-transparent text-white placeholder-gray-400 focus:outline-none"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-between text-sm">
                      <span className={`${amount < selectedPlan.p2p_minimumdonation ? 'text-red-400 font-semibold' : 'text-gray-400'}`}>
                        Min: {money(selectedPlan.p2p_minimumdonation, selectedCurrencyObj?.type)}
                      </span>
                      <span className={`${amount > selectedPlan.p2p_maximumdonation ? 'text-red-400 font-semibold' : 'text-gray-400'}`}>
                        Max: {money(selectedPlan.p2p_maximumdonation, selectedCurrencyObj?.type)}
                      </span>
                    </div>

                    {/* Quick Amount Buttons */}
                    <div className="grid grid-cols-4 gap-2">
                      {[selectedPlan.p2p_minimumdonation, selectedPlan.p2p_minimumdonation * 5, selectedPlan.p2p_minimumdonation * 10, selectedPlan.p2p_maximumdonation].map((quickAmount, index) => (
                        <button
                          key={index}
                          onClick={() => setAmount(quickAmount)}
                          className="py-2 px-3 bg-gray-700 hover:bg-amber-500/20 border border-gray-600 hover:border-amber-500/50 rounded-lg text-white text-sm font-medium transition-all duration-300"
                        >
                          {money(quickAmount, selectedCurrencyObj?.type)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Investment Button */}
                  <button
                    onClick={submitInvestment}
                    disabled={!isAmountValid || submittingInvestment}
                    className={`w-full py-6 rounded-2xl font-bold text-xl transition-all duration-300 flex items-center justify-center space-x-3 ${
                      isAmountValid && !submittingInvestment
                        ? `bg-gradient-to-r ${getTierGradient(selectedPlan.tier)} hover:shadow-2xl hover:scale-105 text-white shadow-lg`
                        : 'bg-gray-600 cursor-not-allowed text-gray-400'
                    }`}
                  >
                    {submittingInvestment ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        <span>Please wait...</span>
                      </>
                    ) : (
                      <>
                        {recommitFor ? <RefreshCw className="h-6 w-6" /> : <Play className="h-6 w-6" />}
                        <span>{recommitFor ? 'Recommit Now' : 'Invest Now'}</span>
                        <ChevronRight className="h-6 w-6" />
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Analytics & Returns */}
          <div className="col-span-12 lg:col-span-6 space-y-6">
            
            {/* Live Returns Calculator */}
            <div className="bg-gray-900/60 backdrop-blur-xl border border-amber-500/20 rounded-2xl p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Brain className="h-6 w-6 text-amber-400" />
                <h3 className="text-xl font-bold text-white">Live Calculator</h3>
              </div>
              
              {selectedPlan && isAmountValid ? (
                <div className="space-y-4">
                  <div className="text-center p-4 bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-xl">
                    <p className="text-emerald-400 text-sm font-medium">Projected Returns</p>
                    <p className="text-2xl font-black text-white">
                      {money(projectedReturns.returns, selectedCurrencyObj?.type)}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Select network & amount</p>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
    </WrapUpP2P>
  );
};

export default P2PInvestmentCommandCenter;